<div class="p-2">
  <!-- Header Section -->
  <div class="flex flex-wrap justify-between gap-2 items-center mb-6">
    <div>
      <h1 class="text-2xl font-bold text-gray-800" i18n>Contract Management</h1>
      <p class="text-gray-600" i18n>Welcome, manage your contracts efficiently.</p>
    </div>
    <!-- <div class="flex gap-3">
      <p-button 
        *hasPermission="'contract.create'"
        label="Add New Contract" 
        icon="pi pi-plus" 
        (onClick)="addNewContract()" 
        [disabled]="loading"
  aria-label="Add new contract to the system" i18n-aria-label>
      </p-button>
    </div> -->
  </div>

  <!-- Search and Filters -->
  <p-card styleClass="mb-6">
    <div class="flex flex-col lg:flex-row gap-4">
      <!-- Search Input -->
      <div class="flex-1">
        <label for="contract-search" class="sr-only" i18n>Search contracts</label>
        <p-iconfield iconPosition="left">
          <p-inputicon>
            <i class="pi pi-search" aria-hidden="true"></i>
          </p-inputicon>
          <input 
            pInputText 
            type="text" 
            id="contract-search"
            i18n-placeholder
            placeholder="Search contracts..." i18n-placeholder
            [(ngModel)]="searchText"
            (keyup.enter)="applyFilters()" 
            class="min-w-[200px]" 
            fluid
            aria-label="Search contracts by reference number, customer name, or other details" i18n-aria-label>
        </p-iconfield>
      </div>

      <!-- Filters -->
      <div class="flex flex-wrap gap-3">
        <p-select 
          [options]="statusOptions" 
          [(ngModel)]="selectedStatus" 
          i18n-placeholder
          placeholder="Status: All" i18n-placeholder
          optionLabel="label"
          optionValue="value" 
          styleClass="min-w-[150px]"
          i18n-aria-label
          [attr.aria-label]="'Filter contracts by status'">
        </p-select>
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-2">
        <p-button 
          i18n-label
          label="Search" 
          icon="pi pi-search" 
          (onClick)="applyFilters()" 
          [disabled]="loading"
          [attr.aria-busy]="loading"
          appGlobalLoadingDisable
          i18n-aria-label
          aria-label="Search and filter contracts" i18n-aria-label>
        </p-button>
        <p-button 
          i18n-label
          label="Reset" 
          icon="pi pi-refresh" 
          severity="secondary" 
          [outlined]="true" 
          (onClick)="resetFilters()"
          [disabled]="loading"
          appGlobalLoadingDisable
          i18n-aria-label
          aria-label="Reset all filters to default values" i18n-aria-label>
        </p-button>
      </div>
    </div>
  </p-card>

  <!-- Contracts Table -->
  <p-card class="!p-3">
    <div class="overflow-x-auto min-h-screen">
      <p-table [value]="contracts" [loading]="loading" [paginator]="false" [rows]="pageSize"
        [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
        [tableStyle]="{'min-width': '1200px', 'width': '100%'}" styleClass="p-datatable-sm">

        <!-- Header -->
        <ng-template pTemplate="header">
          <tr>
            <th class="min-w-[120px]" i18n>REF NO</th>
            <th class="min-w-[200px]" i18n>CUSTOMER</th>
            <th class="min-w-[150px]" i18n>FSR</th>
            <th class="min-w-[100px] text-center" i18n>PRODUCTS</th>
            <th class="min-w-[120px]" i18n>VALUE</th>
            <th class="min-w-[120px]" i18n>END DATE</th>
            <th class="min-w-[120px]" i18n>STATUS</th>
            <th 
              class="min-w-[120px] cursor-pointer hover:bg-gray-50 transition-colors duration-200"
              (click)="toggleSort()"
              (keydown.enter)="toggleSort()"
              (keydown.space)="toggleSort(); $event.preventDefault()"
              tabindex="0"
              role="button"
              [attr.aria-label]="'Sort by created date, currently ' + (sortOrder === -1 ? 'descending' : 'ascending')">
              <div class="flex items-center justify-between">
                <span i18n>CREATED</span>
                <i class="pi" [ngClass]="sortOrder === -1 ? 'pi-sort-down' : 'pi-sort-up'" aria-hidden="true"></i>
              </div>
            </th>
            <th class="min-w-[180px]" i18n>ACTIONS</th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-contract>
          <tr class="cursor-pointer hover:bg-gray-50 transition-colors duration-200" (click)="viewContract(contract)">
            <!-- Ref No -->
            <td class="min-w-[120px]">
              <div class="flex items-center gap-2">
                <div>
                  <div class="text-sm font-medium text-gray-900">{{ contract.refNo  }}</div>
                </div>
              </div>
            </td>

            <!-- Customer -->
            <td class="min-w-[200px]">
              <div class="flex items-center gap-3">
                <div>
                  <div class="text-sm text-gray-500">{{ contract.customerId?.businessName.split(' ').length > 2 ?
                    contract.customerId?.businessName.split(' ').slice(0, 4).join(' ') + '...' :
                    contract.customerId?.businessName }}</div>
                </div>
              </div>
            </td>

            <!-- FSR -->
            <td class="min-w-[150px]">
              <div class="flex items-center gap-2">
                <div>
                  <div class="text-sm text-gray-500">{{ contract.fsrId?.firstName || 'N/A' }} {{ contract.fsrId?.lastName || 'N/A' }}</div>
                </div>
              </div>
            </td>

            <!-- Products Count -->
            <td class="min-w-[100px] text-start">
              <div class="text-start pl-5" 
                   pTooltip="{{ getProductNames(contract.products) }}" 
                   tooltipPosition="top">
                <div class="text-lg font-bold text-blue-600">{{ contract.products?.length || 0 }}</div>
              </div>
            </td>

            <!-- Contract Value -->
            <td class="min-w-[120px]">
              <span class="text-sm text-gray-500">{{ formatCurrency(contract.contractValue) }}</span>
            </td>

            <!-- End Date / Termination Date -->
            <td class="min-w-[120px]">
              <span class="text-sm text-gray-500" *ngIf="contract.status?.toUpperCase() !== 'TERMINATED'">{{ formatDate(contract.endDate) }}</span>
              <span class="text-sm text-gray-500" *ngIf="contract.status?.toUpperCase() === 'TERMINATED'">
                <span class="text-red-600 font-medium" i18n>Terminated:</span> {{ formatDate(contract.terminationDate || contract.updatedAt) }}
              </span>
            </td>

            <!-- Status -->
            <td class="min-w-[120px]">
              <p-tag [value]="getStatusLabel(contract.status, contract.endDate)" [severity]="getStatusBadgeSeverity(contract.status, contract.endDate)">
              </p-tag>
            </td>

            <!-- Created Date -->
            <td class="min-w-[120px]">
              <span class="text-sm text-gray-500">{{ formatDate(contract.createdAt) }}</span>
            </td>

            <!-- Actions -->
            <td class="min-w-[180px]" (click)="$event.stopPropagation()">
              <div class="flex items-center gap-2">
                <!-- Conditional Actions based on Status - Hide from FSR users -->
                <div *ngIf="contract.status?.toUpperCase() === 'DRAFT' && !isFSR()">
                  <p-button 
                    *hasPermission="'contract.review'"
                    i18n-label
                    label="Review Contract" 
                    size="small" 
                    [outlined]="true" 
                    (onClick)="reviewContract(contract)"
                    [attr.aria-label]="'Review contract ' + contract.refNo">
                  </p-button>
                </div>
                
                <!-- Show restricted message for FSR users with draft contracts -->
                <div *ngIf="contract.status?.toUpperCase() === 'DRAFT' && isFSR()" class="text-sm text-gray-500 italic">
                  <span i18n>Currently Under Review</span>
                </div>

                <!-- Update Contract button for non-DRAFT and non-TERMINATED contracts -->
                <div *ngIf="contract.status?.toUpperCase() !== 'DRAFT' && contract.status?.toUpperCase() !== 'TERMINATED'" class="flex items-center gap-2">
                  <p-button 
                    appGlobalLoadingDisable 
                    i18n-label
                    label="Update Contract" 
                    icon="pi pi-pencil" 
                    size="small" 
                    [outlined]="true"
                    (onClick)="updateContract(contract)" 
                    [attr.aria-label]="'Update contract ' + contract.refNo">
                  </p-button>
                </div>
              </div>
            </td>
          </tr>
        </ng-template>

        <!-- Empty State -->
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="9" class="text-center py-8">
              <div class="flex flex-col items-center gap-2" role="status">
                <i class="pi pi-file-text text-4xl text-gray-300" aria-hidden="true"></i>
                <p class="text-gray-500" i18n>No contracts found</p>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>

      <!-- Pagination -->
      <nav class="flex flex-wrap justify-between gap-2 mt-4" aria-label="Contract list pagination">
        <div class="text-sm text-gray-600" role="status" aria-live="polite">
          <span i18n>Showing</span> {{ (currentPage) * pageSize + 1 }}<span i18n> to </span>{{ Math.min((currentPage + 1) * pageSize, totalContracts) }}<span i18n> of </span>{{ totalContracts }}<span i18n> entries</span>
        </div>

        <p-paginator 
          [rows]="pageSize" 
          [totalRecords]="totalContracts" 
          [rowsPerPageOptions]="pageSizes"
          (onPageChange)="onPageChange($event)" 
          [showCurrentPageReport]="false"
          [attr.aria-label]="'Navigate through contract pages'">
        </p-paginator>
      </nav>
    </div>
  </p-card>

  <p-menu 
    #menu 
    [popup]="true" 
    [model]="actionMenuItems" 
    appendTo="body"
    role="menu"
    [attr.aria-label]="'Contract actions menu'">
  </p-menu>

  <!-- Contract Status Update Loading Overlay -->
  <div *ngIf="updatingContractStatus" class="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50">
    <app-loading-spinner size="large" type="full-screen" i18n-message message="Updating contract status..."></app-loading-spinner>
  </div>

  <!-- Contract Review Drawer -->
  <p-drawer 
    [(visible)]="isDrawerVisible" 
    position="right" 
    [modal]="true" 
    [style]="{width: '90vw', maxWidth: '600px'}"
    (onHide)="closeContractReview()"
    role="dialog"
    [attr.aria-label]="'Contract review panel'"
    [attr.aria-modal]="true">

    <ng-template pTemplate="header">
      <div class="flex items-center justify-between w-full">
        <h3 class="text-xl font-bold text-gray-800" id="contract-review-heading" i18n>Contract Information</h3>
      </div>
    </ng-template>

    <ng-template pTemplate="content">
      <div *ngIf="selectedContractForReview" class="space-y-6">
        <!-- Contract Header -->
        <div class="mb-6">
          <div class="flex justify-between items-start mb-4">
            <div>
              <h2 class="text-2xl font-bold text-gray-800 mb-2">
                {{ getCustomerName(selectedContractForReview) }}
              </h2>

            </div>
            <div class="text-right w-full">
              <p-tag [value]="getStatusLabel(selectedContractForReview.status, selectedContractForReview.endDate)"
                [severity]="getStatusBadgeSeverity(selectedContractForReview.status, selectedContractForReview.endDate)" styleClass="text-sm">
              </p-tag>
              <p class="text-sm text-gray-500 mt-1 w-full" *ngIf="selectedContractForReview.status?.toUpperCase() !== 'TERMINATED'">
                <span i18n>Valid till</span>: {{ formatDate(selectedContractForReview.endDate) }}
              </p>
              <p class="text-sm text-red-600 mt-1 w-full font-medium" *ngIf="selectedContractForReview.status?.toUpperCase() === 'TERMINATED'">
                <span i18n>Terminated on</span>: {{ formatDate(selectedContractForReview.terminationDate || selectedContractForReview.updatedAt) }}
              </p>
            </div>
          </div>
        </div>

        <!-- Contract Value -->
        <div class="mb-6">
          <div class="flex justify-between items-center border border-[#0061DD] py-3 px-4 rounded-xl bg-[#F3F8FF]">
            <span class="text-sm font-medium text-gray-700" i18n>Contract Value</span>
            <span class="text-lg font-semibold text-gray-900">
              {{ formatCurrency(selectedContractForReview.contractValue) }}
            </span>
          </div>
        </div>

        <!-- Products -->
        <div class="mb-6">
          <div class="flex justify-between items-center mb-3">
            <span class="text-sm font-medium text-gray-700" i18n>Products</span>
          </div>
          
          <!-- Product Values List (when product values are available) -->
          <div *ngIf="hasProductValues(selectedContractForReview)" class="space-y-2 mb-3">
            <div *ngFor="let product of selectedContractForReview.products" class="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div class="flex items-center gap-3 flex-1">
                <p-tag [value]="getProductName(product)" styleClass="text-xs"></p-tag>
                <span class="text-sm text-gray-600" i18n>Value:</span>
              </div>
              <span class="text-sm font-semibold text-gray-900">
                {{ getProductValue(product, selectedContractForReview.productValues) }}
              </span>
            </div>
          </div>
          
          <!-- Products Tags (always shown) -->
          <div *ngIf="!(hasProductValues(selectedContractForReview))" class="flex flex-wrap gap-2">
            <p-tag *ngFor="let product of selectedContractForReview.products" [value]="getProductName(product)"
              styleClass="text-xs">
            </p-tag>
          </div>
        </div>

        <!-- Contract Description -->
        <div class="mb-6">
          <div class="">

            <!-- Contract Description -->
            <div class="break-words overflow-wrap-anywhere"
              [innerHTML]="sanitizeHtml(selectedContractForReview.description || '')">
              <!-- Fallback content if innerHTML doesn't work -->
              <div *ngIf="!selectedContractForReview.description" class="text-gray-500 italic">
                <span i18n>No description available</span>
              </div>
            </div>
          </div>
        </div>

        <hr class="h-[1px] text-[#D9D9D9]">

        <!-- Attachments -->
        <div class="mb-6" *ngIf="selectedContractForReview.documents && selectedContractForReview.documents.length > 0">
          <div class="mb-3">
            <span class="text-sm font-medium text-gray-700" i18n>Attachments</span>
          </div>
          <div class="space-y-2">
            <div *ngFor="let doc of selectedContractForReview.documents; let i = index" class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <i [class]="getFileIcon(doc).icon + ' ' + getFileIcon(doc).color"></i>
                <span class="text-sm text-gray-700">{{ getDocumentDisplayName(doc, i) }}</span>
              </div>
              <button class="text-blue-600 hover:text-blue-800 cursor-pointer" (click)="openDocument(doc)">
                <i class="pi pi-external-link"></i>
              </button>
            </div>
          </div>
        </div>

        <hr class="h-[1px] text-[#D9D9D9]">

        <!-- Contract Details Grid -->
        <div class="grid grid-cols-2 gap-6">
          <div class="space-y-4">
            <div>
              <span class="text-xs text-gray-500 block">Customer</span>
              <span class="text-sm font-medium text-gray-900">
                {{ getCustomerName(selectedContractForReview).split(' ').length > 2 ?
                getCustomerName(selectedContractForReview).split(' ').slice(0, 5).join(' ') + '...' :
                getCustomerName(selectedContractForReview) }}
              </span>
            </div>
            <div>
              <span class="text-xs text-gray-500 block">FSR</span>
              <span class="text-sm font-medium text-gray-900">
                {{ getFSRName(selectedContractForReview) }}
              </span>
            </div>
            <div>
              <span class="text-xs text-gray-500 block">Start Date</span>
              <span class="text-sm font-medium text-gray-900">
                {{ formatDate(selectedContractForReview.startDate) }}
              </span>
            </div>
          </div>

          <div class="space-y-4">
            <div>
              <span class="text-xs text-gray-500 block">Reservation No</span>
              <span class="text-sm font-medium text-gray-900">
                <span class="hidden sm:inline">{{ getReservationRefNo(selectedContractForReview) || 'N/A' }}</span>
                <span class="sm:hidden">{{ (getReservationRefNo(selectedContractForReview) || 'N/A').substring(0, 15) }}{{ (getReservationRefNo(selectedContractForReview) || 'N/A').length > 20 ? '...' : '' }}</span>
              </span>
            </div>
            <div>
              <span class="text-xs text-gray-500 block">Approved By</span>
              <span class="text-sm font-medium text-gray-900">
                {{ getApproverName(selectedContractForReview) }}
              </span>
            </div>
            <div>
              <span class="text-xs text-gray-500 block" *ngIf="selectedContractForReview.status?.toUpperCase() !== 'TERMINATED'">End Date</span>
              <span class="text-xs text-gray-500 block text-red-600" *ngIf="selectedContractForReview.status?.toUpperCase() === 'TERMINATED'">Termination Date</span>
              <span class="text-sm font-medium text-gray-900" *ngIf="selectedContractForReview.status?.toUpperCase() !== 'TERMINATED'">
                {{ formatDate(selectedContractForReview.endDate) }}
              </span>
              <span class="text-sm font-medium text-red-600" *ngIf="selectedContractForReview.status?.toUpperCase() === 'TERMINATED'">
                {{ formatDate(selectedContractForReview.terminationDate || selectedContractForReview.updatedAt) }}
              </span>
            </div>
          </div>

          @if (selectedContractForReview.status === 'DRAFT' && !isFSR()) {
          <div class="flex flex-col md:flex-row gap-3 w-full">
            <p-button 
              *hasPermission="'contract.review'"
              i18n-label
              label="Approve Contract" 
              icon="pi pi-check" 
              class="whitespace-nowrap"
              (onClick)="approveContract(selectedContractForReview._id)">
            </p-button>

            <p-button 
              *hasPermission="'contract.review'"
              i18n-label
              label="Deny Contract" 
              icon="pi pi-times" 
              class="whitespace-nowrap" 
              [outlined]="true"
              (onClick)="denyContract(selectedContractForReview._id)">
            </p-button>
          </div>
          }

          @if ((selectedContractForReview.status === 'ACTIVE' || (selectedContractForReview.status === 'ACTIVE' && isExpiringSoon(selectedContractForReview.endDate))) && !isFSR()) {
          <div class="flex gap-3 w-full">
            <p-button i18n-label label="Terminate Contract" icon="pi pi-times-circle" class="whitespace-nowrap" severity="danger" [outlined]="true"
              *hasPermission="'contract.terminate'"
              (onClick)="terminateContract(selectedContractForReview._id)">
            </p-button>
          </div>
          }

          @if (selectedContractForReview.status === 'TERMINATED' && canCreateNewContract[selectedContractForReview._id] !== false) {
          <div class="flex gap-3 w-full">
            <p-button 
              *ngIf="canCreateNewContract[selectedContractForReview._id] === true"
              i18n-label
              label="Add New Contract for This Customer" 
              icon="pi pi-plus" 
              class="whitespace-nowrap"
              (onClick)="addNewContractForCustomer(selectedContractForReview)">
            </p-button>
            <div *ngIf="checkingContractCreation[selectedContractForReview._id]" class="flex items-center gap-2 text-sm text-gray-500">
              <i class="pi pi-spin pi-spinner"></i>
              <span i18n>Checking available products...</span>
            </div>
            <div *ngIf="canCreateNewContract[selectedContractForReview._id] === false && !checkingContractCreation[selectedContractForReview._id]" class="text-sm text-gray-500 italic">
              <span i18n>All products from this reservation already have active contracts</span>
            </div>
          </div>
          }

          @if (selectedContractForReview.status === 'REJECTED' && isFSR()) {
          <div class="flex gap-3 w-full">
            <p-button 
              i18n-label
              label="Resubmit Contract" 
              icon="pi pi-refresh" 
              class="whitespace-nowrap"
              (onClick)="resubmitContract(selectedContractForReview._id)">
            </p-button>
            <p-button 
              *ngIf="selectedContractForReview.status?.toUpperCase() !== 'TERMINATED'"
              i18n-label
              label="Edit Contract" 
              icon="pi pi-pencil" 
              class="whitespace-nowrap" 
              [outlined]="true"
              (onClick)="updateContract(selectedContractForReview)">
            </p-button>
          </div>
          }
        </div>

        <!-- Termination History -->
        @if (selectedContractForReview.terminationHistory && selectedContractForReview.terminationHistory.length > 0) {
        <hr class="h-[1px] text-[#D9D9D9] my-6">
        <div class="mb-6">
          <h4 class="text-lg font-semibold text-gray-800 mb-4" i18n>Termination History</h4>
          <div class="space-y-4">
            @for (termination of selectedContractForReview.terminationHistory; track termination.terminatedAt) {
            <div class="p-4 bg-red-50 border-l-4 border-red-500 rounded">
              <div class="flex items-start justify-between mb-2">
                <div class="flex items-center gap-2">
                  <i class="pi pi-times-circle text-red-500"></i>
                  <span class="font-semibold text-red-900" i18n>Terminated</span>
                </div>
                <span class="text-sm text-gray-600">{{ formatDateTime(termination.terminatedAt) }}</span>
              </div>
              <div class="mt-2">
                <p class="text-sm font-medium text-gray-700 mb-1" i18n>Reason:</p>
                <p class="text-sm text-gray-800">{{ termination.reason }}</p>
              </div>
              @if (termination.terminatedByName) {
              <div class="mt-2 text-xs text-gray-600">
                <span i18n>Terminated by:</span> {{ termination.terminatedByName }}
              </div>
              }
            </div>
            }
          </div>
        </div>
        }

        <!-- Rejection History and Timeline -->
        @if (selectedContractForReview.rejectionHistory && selectedContractForReview.rejectionHistory.length > 0) {
        <hr class="h-[1px] text-[#D9D9D9] my-6">
        <div class="mb-6">
          <h4 class="text-lg font-semibold text-gray-800 mb-4" i18n>Rejection History</h4>
          <div class="space-y-4">
            @for (rejection of selectedContractForReview.rejectionHistory; track rejection.rejectedAt) {
            <div class="p-4 bg-red-50 border-l-4 border-red-500 rounded">
              <div class="flex items-start justify-between mb-2">
                <div class="flex items-center gap-2">
                  <i class="pi pi-times-circle text-red-500"></i>
                  <span class="font-semibold text-red-900" i18n>Rejected</span>
                </div>
                <span class="text-sm text-gray-600">{{ formatDateTime(rejection.rejectedAt) }}</span>
              </div>
              <div class="mt-2">
                <p class="text-sm font-medium text-gray-700 mb-1" i18n>Reason:</p>
                <p class="text-sm text-gray-800">{{ rejection.reason }}</p>
              </div>
              @if (rejection.rejectedByName) {
              <div class="mt-2 text-xs text-gray-600">
                <span i18n>Rejected by:</span> {{ rejection.rejectedByName }}
              </div>
              }
            </div>
            }
          </div>
        </div>
        }

        <!-- Submission Timeline -->
        @if (selectedContractForReview.submissionHistory && selectedContractForReview.submissionHistory.length > 0) {
        <hr class="h-[1px] text-[#D9D9D9] my-6">
        <div class="mb-6">
          <h4 class="text-lg font-semibold text-gray-800 mb-4" i18n>Submission Timeline</h4>
          <div class="space-y-3">
            @for (submission of selectedContractForReview.submissionHistory; track submission.submittedAt) {
            <div class="flex items-start gap-3 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
              <i class="pi pi-send text-blue-500 mt-1"></i>
              <div class="flex-1">
                <div class="flex items-center justify-between">
                  <span class="font-semibold text-blue-900" i18n>Submitted</span>
                  <span class="text-sm text-gray-600">{{ formatDateTime(submission.submittedAt) }}</span>
                </div>
                <p class="text-xs text-gray-600 mt-1" i18n>Status: {{ submission.status }}</p>
              </div>
            </div>
            }
          </div>
        </div>
        }
      </div>

    </ng-template>
  </p-drawer>

  <!-- Rejection Reason Dialog -->
  <p-dialog 
    [(visible)]="showRejectionDialog" 
    [modal]="true" 
    [style]="{width: '500px'}"
    [closable]="true"
    (onHide)="cancelRejection()"
    header="Reject Contract"
    i18n-header>
    <div class="space-y-4">
      <p class="text-gray-700" i18n>Please provide a reason for rejecting this contract. This reason will be visible to the FSR.</p>
      <div>
        <label for="rejectionReason" class="block text-sm font-medium text-gray-700 mb-2" i18n>Rejection Reason *</label>
        <textarea 
          id="rejectionReason"
          pInputTextarea 
          [(ngModel)]="rejectionReason"
          placeholder="Enter the reason for rejection..."
          i18n-placeholder
          rows="5"
          class="w-full"
          [autoResize]="true">
        </textarea>
      </div>
    </div>
    <ng-template pTemplate="footer">
      <div class="flex gap-2 justify-end">
        <p-button 
          label="Cancel" 
          icon="pi pi-times" 
          [outlined]="true"
          (onClick)="cancelRejection()"
          i18n-label>
        </p-button>
        <p-button 
          label="Confirm Rejection" 
          icon="pi pi-check" 
          severity="danger"
          (onClick)="confirmRejection()"
          [disabled]="!rejectionReason || rejectionReason.trim() === ''"
          i18n-label>
        </p-button>
      </div>
    </ng-template>
  </p-dialog>

  <!-- Termination Reason Dialog -->
  <p-dialog 
    [(visible)]="showTerminationDialog" 
    [modal]="true" 
    [style]="{width: '500px'}"
    [closable]="true"
    (onHide)="cancelTermination()"
    header="Terminate Contract"
    i18n-header>
    <div class="space-y-4">
      <p class="text-gray-700" i18n>Please provide a reason for terminating this contract. This reason will be visible to the FSR.</p>
      <div>
        <label for="terminationReason" class="block text-sm font-medium text-gray-700 mb-2" i18n>Termination Reason</label>
        <textarea 
          id="terminationReason"
          pInputTextarea 
          [(ngModel)]="terminationReason"
          placeholder="Enter the reason for termination (optional)..."
          i18n-placeholder
          rows="5"
          class="w-full"
          [autoResize]="true">
        </textarea>
      </div>
    </div>
    <ng-template pTemplate="footer">
      <div class="flex gap-2 justify-end">
        <p-button 
          label="Cancel" 
          icon="pi pi-times" 
          [outlined]="true"
          (onClick)="cancelTermination()"
          i18n-label>
        </p-button>
        <p-button 
          label="Confirm Termination" 
          icon="pi pi-check" 
          severity="danger"
          (onClick)="confirmTermination()"
          i18n-label>
        </p-button>
      </div>
    </ng-template>
  </p-dialog>

  <!-- Confirmation Dialogs -->
  <p-confirmDialog></p-confirmDialog>

  <!-- Toast Messages -->
  <p-toast></p-toast>
</div>